import { useCallback } from 'react';
import { useLang, type Lang } from './language';

/**
 * A legacy lookup row that carries both scripts — categories, branches, items,
 * cashiers. The Matix schema stores the Arabic name in a parallel `*_ar`
 * column rather than in a translation table, so every display of one of these
 * has to make the same choice.
 */
export interface BilingualRow {
  name?: string | null;
  name_ar?: string | null;
}

/**
 * The name to show for the active language.
 *
 * Falls back to the other script rather than showing nothing: `name_ar` is
 * optional in the legacy schema and plenty of tenants have filled in only one
 * of the two, so an Arabic till must still be able to read a category whose
 * Arabic name was never entered. Blank counts as absent — legacy rows store
 * `''` about as often as NULL.
 */
export function nameFor(lang: Lang, row: BilingualRow, fallback = ''): string {
  const en = row.name?.trim() ?? '';
  const ar = row.name_ar?.trim() ?? '';
  return (lang === 'ar' ? ar || en : en || ar) || fallback;
}

/**
 * Both names of an item, ordered for the active language: `primary` is the
 * one the cashier reads — Arabic on an Arabic till, English on an English one
 * — and is shown bold and large; `secondary` is the other script, shown small
 * and muted under it. `secondary` is null when there is no second name or it
 * says the same thing, so nothing is printed twice. Falls back like `nameFor`.
 */
export function namePair(
  lang: Lang,
  row: BilingualRow,
  fallback = '',
): { primary: string; secondary: string | null } {
  const en = row.name?.trim() ?? '';
  const ar = row.name_ar?.trim() ?? '';
  const primary = (lang === 'ar' ? ar || en : en || ar) || fallback;
  const other = lang === 'ar' ? en : ar;
  return { primary, secondary: other && other !== primary ? other : null };
}

/** `nameFor` bound to the active language, for use inside components. */
export function useLocalizedName(): (row: BilingualRow, fallback?: string) => string {
  const { lang } = useLang();
  return useCallback(
    (row: BilingualRow, fallback?: string) => nameFor(lang, row, fallback),
    [lang],
  );
}
